import { useState } from 'react';

import { getDefaultExpandedSections, hasDefaultExpandedSectionIdsChanged } from 'helpers/QuickFilterUtils';
import type { NavigationItem } from 'types/search';

interface UseQuickFilterSectionExpansionOptions {
  activeSectionId?: string;
  /** Size section defaults; when a response changes them, the previous defaults close and the new ones open. */
  defaultExpandedSectionIds?: readonly string[];
  isLoading: boolean;
  sections: NavigationItem[];
}

interface UseQuickFilterSectionExpansionReturn {
  expandedSections: Set<string>;
  handleExpandedSectionsChange: (values: string[]) => void;
}

/**
 * Controls which Drawer sections a Quick Filter Pill shows expanded.
 * Opens a pill's default sections and swaps them when they change, and expands a ZC section whenever it becomes active without collapsing others.
 */
export const useQuickFilterSectionExpansion = ({
  activeSectionId,
  defaultExpandedSectionIds,
  isLoading,
  sections
}: UseQuickFilterSectionExpansionOptions): UseQuickFilterSectionExpansionReturn => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initialExpandedSections = getDefaultExpandedSections(sections, activeSectionId);
    defaultExpandedSectionIds?.forEach(sectionId => initialExpandedSections.add(sectionId));

    return initialExpandedSections;
  });
  const [lastActiveSectionId, setLastActiveSectionId] = useState(activeSectionId);
  const [lastDefaultExpandedSectionIds, setLastDefaultExpandedSectionIds] = useState(defaultExpandedSectionIds);

  // When the active section changes, record it and expand it once.
  if (activeSectionId !== lastActiveSectionId) {
    setLastActiveSectionId(activeSectionId);

    if (activeSectionId) {
      setExpandedSections(prevExpandedSections =>
        prevExpandedSections.has(activeSectionId) ? prevExpandedSections : new Set(prevExpandedSections).add(activeSectionId)
      );
    }
  }

  // When the defaults change, close the previous defaults and open the new ones.
  if (hasDefaultExpandedSectionIdsChanged(defaultExpandedSectionIds, lastDefaultExpandedSectionIds)) {
    setLastDefaultExpandedSectionIds(defaultExpandedSectionIds);
    setExpandedSections(prevExpandedSections => {
      const nextExpandedSections = new Set(prevExpandedSections);
      lastDefaultExpandedSectionIds?.forEach(sectionId => nextExpandedSections.delete(sectionId));
      defaultExpandedSectionIds?.forEach(sectionId => nextExpandedSections.add(sectionId));

      return nextExpandedSections;
    });
  }

  const handleExpandedSectionsChange = (values: string[]) => {
    if (isLoading) {
      return;
    }

    setExpandedSections(new Set(values));
  };

  return {
    expandedSections,
    handleExpandedSectionsChange
  };
};
